import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { PostContext } from "../../contextStore/PostContext";
import "./postcards.css";

// Optional: if you have an API_BASE constant in env and want placeholder to come from it,
// uncomment and set REACT_APP_API_BASE in your build environment.
// const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const PLACEHOLDER = "/placeholder-product.png";

function makeRelativeUploadUrl(urlOrPath) {
  if (!urlOrPath) return null;
  const s = String(urlOrPath).trim();
  // Try parse absolute URL
  try {
    const u = new URL(s);
    // If it points to /uploads/... convert to same-origin path
    if (u.pathname && u.pathname.startsWith("/uploads/")) {
      // return the pathname + search to preserve query strings
      return u.pathname + (u.search || "");
    }
    // Keep external CDN URLs untouched
    return s;
  } catch (e) {
    // Not absolute — normalize common relative forms
    if (s.startsWith("/uploads/")) return s;
    if (s.startsWith("uploads/")) return `/${s}`;
    // If it's just a filename, map to /uploads/<filename>
    return `/uploads/${s}`;
  }
}

/**
 * Pick a likely image field from the product object.
 * It returns a raw string which then should be normalized by makeRelativeUploadUrl.
 */
function pickImageCandidate(product) {
  if (!product || typeof product !== "object") return null;

  // images array: items may be objects or strings
  if (Array.isArray(product.images) && product.images.length) {
    const first = product.images[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object") {
      return first.url || first.path || first.file || first.filename || first.name || null;
    }
  }

  // Some APIs use media array
  if (Array.isArray(product.media) && product.media.length) {
    const m = product.media[0];
    if (!m) return null;
    if (typeof m === "string") return m;
    if (typeof m === "object") return m.url || m.path || m.file || null;
  }

  // Common top-level fields
  const possible = [
    "url",
    "image",
    "image_url",
    "photo",
    "thumbnail",
    "file",
    "main_image",
    "mainImage",
    "imagePath",
  ];
  for (const k of possible) {
    if (product[k]) return product[k];
  }

  // some backends store filename or path in other names
  if (product.filename) return product.filename;
  if (product.path) return product.path;

  return null;
}

export default function PostCards({ product }) {
  const { setPostContent } = useContext(PostContext);
  const navigate = useNavigate();

  const id = product?.id || product?._id || null;
  const rawCandidate = pickImageCandidate(product);
  const imageUrl = makeRelativeUploadUrl(rawCandidate) || PLACEHOLDER;

  const title = product?.title || product?.name || "Unnamed Machine";
  const price = product?.price || 0;
  const city =
    product?.location_city ||
    product?.city ||
    product?.location_state ||
    "";
  const category =
    product?.industry_name ||
    (Array.isArray(product?.industries) ? product.industries[0] : product?.industries) ||
    product?.industry ||
    product?.category ||
    "—";

  const fmtRupee = (val) => {
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(Number(val || 0));
    } catch {
      return `₹${val || 0}`;
    }
  };

  const handleClick = () => {
    if (typeof setPostContent === "function") setPostContent(product);
    if (id) navigate(`/machines/${id}`);
    else navigate("/view");
  };

  return (
    <div
      className="postcard-inner"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
    >
      <div className="postcard-thumb">
        <img
          src={imageUrl}
          alt={title}
          loading="lazy"
          onError={(e) => {
            // on error, fallback to placeholder
            e.currentTarget.onerror = null;
            e.currentTarget.src = PLACEHOLDER;
          }}
        />
      </div>

      <div className="postcard-body">
        <h3 className="postcard-title" title={title}>
          {title}
        </h3>

        <p className="postcard-price">{fmtRupee(price)}</p>

        <div className="postcard-meta">
          <span className="postcard-category">{category}</span>
          {city && <span className="postcard-location">📍 {city}</span>}
        </div>
      </div>
    </div>
  );
}
